// src/components/home/UpForGrabsHomeCard.jsx
// A can't-miss box at the top of the agent dashboard listing open houses that
// are up for grabs RIGHT NOW. Renders nothing when no slots are open. Agents
// request to host straight from here via the existing RequestToHostModal.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock } from "lucide-react";
import { useStateValue } from "../../context/StateProvider";
import { subscribeOpportunities } from "../../utils/firebasefunctions";
import { REQUEST_STATUS } from "../../utils/openHouseOpportunityHelpers";
import { liveOpenOpportunities } from "../../utils/openHouseHelpers";
import RequestToHostModal from "../open-houses/RequestToHostModal";

export default function UpForGrabsHomeCard() {
  const [{ user, deals }] = useStateValue();
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [requestTarget, setRequestTarget] = useState(null);

  useEffect(() => {
    const unsub = subscribeOpportunities(setOpportunities);
    return () => unsub && unsub();
  }, []);

  const openOps = useMemo(
    () => liveOpenOpportunities(opportunities, deals),
    [opportunities, deals],
  );

  if (openOps.length === 0) return null;

  const hasMyPending = (o) =>
    (o.requests || []).some(
      (r) => r.agentId === user?.id && r.status === REQUEST_STATUS.PENDING,
    );

  return (
    <div className="px-3 lg:px-6 pt-3 lg:pt-6">
      <div className="bg-white border-2 border-[#1E293B] rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between bg-[#1E293B] px-5 py-3">
          <div className="flex items-center gap-2 text-white">
            <CalendarClock size={18} />
            <h2 className="font-bold text-sm sm:text-base">
              Open Houses Up For Grabs
            </h2>
            <span className="bg-white/15 text-white text-xs font-semibold rounded-full px-2 py-0.5">
              {openOps.length}
            </span>
          </div>
          <button
            onClick={() => navigate("/dashboard/open-houses")}
            className="text-white/80 hover:text-white text-xs font-semibold"
          >
            View board
          </button>
        </div>
        <div className="divide-y divide-slate-100">
          {openOps.map((o) => (
            <div
              key={o.id}
              className="flex flex-wrap items-center gap-2 justify-between px-5 py-3"
            >
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 text-sm truncate">
                  {(o.propertyAddress || "").split(",")[0] || "Listing"}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {[
                    o.suggestedTiming ? `Timing: ${o.suggestedTiming}` : "",
                    o.postedByName ? `Posted by ${o.postedByName}` : "",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              {hasMyPending(o) ? (
                <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5">
                  Requested
                </span>
              ) : (
                <button
                  onClick={() => setRequestTarget(o)}
                  className="bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 rounded-md whitespace-nowrap"
                >
                  Request to Host
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <RequestToHostModal
        open={!!requestTarget}
        opportunity={requestTarget}
        onClose={() => setRequestTarget(null)}
        onSubmitted={() => setRequestTarget(null)}
      />
    </div>
  );
}
